import { AfterViewInit, Component, OnInit } from '@angular/core';
import { MatDialog } from '@angular/material/dialog';
import { HttpClient } from '@angular/common/http';
import { AuthService, hashIdentifier } from '../../services/auth.service';
import { OAuthService, OAuthProvider } from '../../services/oauth.service';
import { LcSelectorModalComponent } from '../lc-selector-modal/lc-selector-modal.component';
import { OtpVerificationModalComponent } from '../otp-verification-modal/otp-verification-modal.component';
import { TotpSetupModalComponent } from '../totp-setup-modal/totp-setup-modal.component';
import { CookieService } from 'ngx-cookie-service';
import { SystemGlobalsService } from '../../../system-globals.service';

@Component({
  selector: 'app-login',
  templateUrl: './login.component.html',
  styleUrls: ['./login.component.scss']
})
export class LoginComponent implements OnInit, AfterViewInit {
  email = '';
  password = '';
  rememberMe = false;
  errorMessage = '';
  isLoading = false;
  useNewLoginUI = true;
  showForgotPassword = false;
  showPassword = false;
  resetMessage = '';
  hasResetError = false;
  switchingToClassic = false;
  public system: string;
  public company: string;
  public isLoggedIn = false;
  public oauthProviders: OAuthProvider[] = [];
  /** Google/Microsoft SSO block: server `ENABLE_SSO` on and at least one provider configured. */
  public ssoSectionVisible = false;

  /** Same as sign-in: hide chrome when embedded; show store / IE alerts when applicable */
  isInIframe = false;
  showStoreMessage = false;
  isIe11 = false;

  // Temporary token to pass between login steps (e.g., after initial login before 2FA)
  private tempAuthToken: string | null = null;
  // Store the selected network ID if multiple LCs are returned
  private selectedNetworkId: number | null = null;
  // Flag to track if we're in switch LC mode
  private isSwitchLcMode: boolean = false;
  // Flag to track if we're in OAuth mode
  private isOAuthMode: boolean = false;
  // Store OAuth data for LC selection
  private oauthData: any = null;

  constructor(
    private authService: AuthService,
    private oauthService: OAuthService,
    private dialog: MatDialog,
    private cookieService: CookieService,
    public globals: SystemGlobalsService,
    private http: HttpClient
  ) {}

  async ngOnInit(): Promise<void> {
    this.useNewLoginUI = true;
    // Remember me logic
    const username = this.cookieService.get('username');
    if (username) {
      this.email = username;
      this.rememberMe = true;
    }

    // Set system and company for dynamic asset management, with defaults
    this.system = this.globals.constants?.systemName || 'ascent';
    this.company = this.globals.companyName || 'aerostudies';
  

    const loginPreview = new URLSearchParams(window.location.search).get('preview') === '1';
    if (this.globals.constants?.authenticated && !loginPreview) {
      this.isLoggedIn = true;
      window.location.href = '/';
      return;
    }

    this.isInIframe = this.inIframe();
    if (this.globals.getUrlParamByName('info') === 'from-store') {
      this.showStoreMessage = true;
    }
    this.isIe11 = !!window['MSInputMethodContext'] && !!document['documentMode'];

    // Check if this is a switch LC request
    this.handleSwitchLcRequest();
    
    // Check for OAuth response
    this.handleOAuthResponse();
    
    // Load OAuth providers
    await this.loadOAuthProviders();
  }

  ngAfterViewInit(): void {
    window.document.body.style.backgroundColor = 'transparent';
    if (this.isInIframe) {
      setTimeout(() => (window.document.body.style.backgroundColor = 'transparent'), 1000);
    }
  }

  private inIframe(): boolean {
    try {
      return window.self !== window.top;
    } catch {
      return true;
    }
  }

  /**
   * Handles the login form submission.
   * Initiates the login process and handles different backend responses (LC selection, 2FA).
   */
  async onLoginSubmit(): Promise<void> {
    this.errorMessage = '';
    if (!this.email || !this.email.trim() || !this.password || !this.password.trim()) {
      this.errorMessage = 'Please enter your email/username and password.';
      return Promise.resolve();
    }
    this.isLoading = true;
    this.tempAuthToken = null;
    // Remember me logic
    if (this.rememberMe) {
      this.cookieService.set('username', this.email, 365, '/');
    } else {
      this.cookieService.delete('username');
    }

    try {
      const response = await this.authService.login({
        email: this.email,
        password: this.password,
        network_id: this.selectedNetworkId || undefined
      });

      // Handle API login response based on new 2FA flow
      await this.handleApiLoginResponse(response);
    } catch (error) {
      this.errorMessage = 'An unexpected error occurred. Please try again.';
      console.error('Login component error:', error);
      console.error('Full error details:', JSON.stringify(error, null, 2));
    } finally {
      this.isLoading = false;
    }
  }

  /**
   * Handles API login response based on the new 2FA flow
   * @param response The response from the API login endpoint
   */
  private async handleApiLoginResponse(response: any): Promise<void> {
    console.log('API Login Response:', response);

    // Check for trusted device first
    const trustedDevices = JSON.parse(localStorage.getItem('trustedDeviceTokens') || '{}');
    const key = await hashIdentifier(this.email);
    
    // Handle multiple trusted devices
    let hasTrustedDevice = false;
    if (trustedDevices[key]) {
      if (Array.isArray(trustedDevices[key])) {
        hasTrustedDevice = trustedDevices[key].includes(response?.device_token);
      } else {
        // Backward compatibility for single token
        hasTrustedDevice = trustedDevices[key] === response?.device_token;
      }
    }

    // Step 1: Multiple Learning Centers - Show LC selector
    if (response?.users && response.users.length > 0) {
      console.log('Multiple LCs found - showing LC selector');
      this.openLcSelectorModal(response.users);
      return;
    }

    // Step 2: Trusted device - Direct login
    if (response?.token && hasTrustedDevice) {
      console.log('Trusted device detected - direct login');
      this.isLoggedIn = true;
      this.authService.handleSuccessfulLogin(response.token);
      return;
    }

    // Step 3: TOTP Setup Required - Show setup modal
    if (response?.require_totp_setup && response.totp_secret && response.token && response.totp_url) {
      console.log('TOTP setup required - showing setup modal');
      this.tempAuthToken = response.token;
      this.openTotpSetupModal(response.token, response.totp_secret, response.totp_url);
      return;
    }

          // Step 4: OTP Verification Required - Show verification modal
      if (response?.require_otp && response.token && !hasTrustedDevice) {
        console.log('OTP verification required - showing verification modal');
        this.tempAuthToken = response.token;
        this.openOtpVerificationModal(response.token, this.email, this.password);
        return;
      }

    // Step 5: Direct Login Success - No 2FA required
    if (response?.token && response.success) {
      console.log('Direct login successful - no 2FA required');
      this.isLoggedIn = true;
      this.authService.handleSuccessfulLogin(response.token);
      return;
    }

    // Step 6: Error handling
    if (response?.message) {
      console.log('Login error message:', response.message);
      this.errorMessage = response.message;
      return;
    }

    if (response?.error) {
      console.log('Login error:', response.error);
      this.errorMessage = response.error;
      return;
    }

    // Step 7: Fallback error
    console.log('Unknown response format:', response);
    this.errorMessage = 'Invalid login response. Please try again.';
  }

  /**
   * Opens the Learning Center selection modal.
   * @param users List of users/LCs to choose from.
   */
  private openLcSelectorModal(users: { id: number; name: string }[]): void {
    const dialogRef = this.dialog.open(LcSelectorModalComponent, {
      width: '450px',
      data: { users },
      disableClose: true // Prevent closing by clicking outside
    });

    dialogRef.afterClosed().subscribe(selectedNetworkId => {
      if (selectedNetworkId) {
        this.selectedNetworkId = selectedNetworkId;
        if (this.isSwitchLcMode) {
          // For switch LC mode, we already have credentials, so proceed directly
          this.performLoginWithStoredCredentials();
        } else if (this.isOAuthMode) {
          // For OAuth mode, call backend to complete OAuth login with selected network
          this.performOAuthLoginWithSelectedNetwork(selectedNetworkId);
        } else {
          // Re-attempt login with the selected network ID
          this.onLoginSubmit();
        }
      } else {
        // User cancelled LC selection, reset state if necessary
        this.selectedNetworkId = null;
        this.isSwitchLcMode = false;
        this.isOAuthMode = false;
        this.oauthData = null;
        this.errorMessage = 'Learning Center selection cancelled.';
      }
    });
  }

  /**
   * Opens the OTP verification modal.
   * @param token Temporary token for OTP verification.
   * @param email The email/username for trusted device keying.
   * @param password The password for trusted device keying.
   */
  private openOtpVerificationModal(token: string, email: string, password: string): void {
    const dialogRef = this.dialog.open(OtpVerificationModalComponent, {
      width: '450px',
      data: { token, email, password },
      disableClose: true
    });

    dialogRef.afterClosed().subscribe(result => {
      if (!result) {
        // User cancelled or verification failed, clear temp token
        this.tempAuthToken = null;
        this.errorMessage = 'OTP verification cancelled or failed.';
      }
      // If result is true, AuthService already handled redirect
    });
  }

  /**
   * Opens the TOTP setup modal.
   * @param token Temporary token for TOTP setup.
   * @param secret The TOTP secret key.
   * @param totpUrl The URL for the QR code image.
   */
  private openTotpSetupModal(token: string, secret: string, totpUrl: string): void {
    const dialogRef = this.dialog.open(TotpSetupModalComponent, {
      width: '450px',
      data: { token, secret, totpUrl },
      disableClose: true
    });

    dialogRef.afterClosed().subscribe(result => {
      if (!result) {
        // User cancelled or setup failed, clear temp token
        this.tempAuthToken = null;
        this.errorMessage = '2FA setup cancelled or failed.';
      }
      // If result is true, AuthService already handled redirect
    });
  }

  toggleForgotPassword(): void {
    this.resetMessage = '';
    this.showForgotPassword = !this.showForgotPassword;
  }

  /** Matches sign-in: Sign In stays disabled until both fields are filled (shows faded primary, not gray). */
  canSignIn(): boolean {
    const user = (this.email || '').trim();
    const pass = (this.password || '').trim();

    return user.length > 0 && pass.length > 0 && !this.isLoading;
  }

  canResetPassword(): boolean {
    return this.email.length > 0;
  }

  resetPassword(): void {
    if (!this.canResetPassword()) return;
    this.resetMessage = '';
    this.hasResetError = false;
    this.isLoading = true;
    this.authService.resetPassword(this.email).subscribe(
      data => {
        this.isLoading = false;
        this.resetMessage = '<b>A password reset email will be sent shortly.</b><ul><li>WAIT for it — do not click \'reset\' again.</li><li>LOOK in your spam/junk folder.</li></ul>';
        if (data) {
          if (data.hasError) {
            this.resetMessage = data.errorMessage;
            this.hasResetError = true;
          }
        }
      },
      () => {
        this.isLoading = false;
        this.resetMessage = '<b>A password reset email will be sent to you shortly if this was a valid email address.</b>';
      }
    );
  }

  onForgotPassword(): void {
    this.toggleForgotPassword();
  }

  togglePasswordVisibility(): void {
    this.showPassword = !this.showPassword;
  }

  /**
   * Load OAuth providers from service
   */
  private async loadOAuthProviders(): Promise<void> {
    try {
      await this.oauthService.waitForConfig();
      this.oauthProviders = this.oauthService.getProviders();
      this.ssoSectionVisible =
        this.oauthService.isSsoEnabledByServer() && this.oauthProviders.length > 0;
    } catch (error) {
      console.error('Failed to load OAuth providers:', error);
      this.oauthProviders = [];
      this.ssoSectionVisible = false;
    }
  }

  hasProvider(name: string): boolean {
    return this.oauthProviders.some(p => p.name === name);
  }

  /**
   * Handles Google SSO login
   */
  signInWithGoogle(): void {
    this.isLoading = true;
    this.errorMessage = '';
    this.oauthService.initiateOAuthLogin('google');
  }

  /**
   * Handles Microsoft SSO login
   */
  signInWithMicrosoft(): void {
    this.isLoading = true;
    this.errorMessage = '';
    this.oauthService.initiateOAuthLogin('microsoft');
  }

  /**
   * Clear all trusted devices for the current user
   * Useful when localStorage is cleared and user wants to reset trusted devices
   */
  async clearAllTrustedDevices(): Promise<void> {
    try {
      const response = await this.authService.clearTrustedDevices(this.email).toPromise();
      
      if (response?.success) {
        // Also clear localStorage
        localStorage.removeItem('trustedDeviceTokens');
        console.log('All trusted devices cleared successfully');
        this.errorMessage = 'All trusted devices cleared successfully. You will need to verify OTP on next login.';
      } else {
        console.error('Failed to clear trusted devices:', response?.error);
        this.errorMessage = 'Failed to clear trusted devices. Please try again.';
      }
    } catch (error) {
      console.error('Error clearing trusted devices:', error);
      this.errorMessage = 'Error clearing trusted devices. Please try again.';
    }
  }

  switchToNewLoginUI(): void {
    this.useNewLoginUI = true;
    this.authService.setUseNewLoginUI(true);
  }

  switchToOldLoginUI(): void {
    this.useNewLoginUI = false;
    this.authService.setUseNewLoginUI(false);
    window.location.reload();
  }

  onSwitchClassicClick() {
    if (this.switchingToClassic) return;
    this.switchingToClassic = true;
    setTimeout(() => {
      this.switchToOldLoginUI();
    }, 500);
  }

  get companyAlt(): string {
    if (!this.company) return 'Company Logo';
    return this.company.charAt(0).toUpperCase() + this.company.slice(1) + ' Logo';
  }

  /**
   * Handles OAuth response from URL parameters
   */
  private handleOAuthResponse(): void {
    const urlParams = new URLSearchParams(window.location.search);
    const oauthSession = urlParams.get('oauth_session');
    const oauthError = urlParams.get('oauth_error');
    const oauthToken = urlParams.get('token');

    if (oauthSession) {
      // Fetch OAuth response from session
      this.fetchOAuthResponseFromSession(oauthSession);
    } else if (oauthError) {
      // OAuth error
      this.errorMessage = decodeURIComponent(oauthError);
    } else if (oauthToken) {
      // Legacy SSO redirect (?token= on new-login); completes session without api-login / 2FA
      window.location.replace(`/auth/token-login?token=${encodeURIComponent(oauthToken)}`);
    }
  }

  /**
   * Fetch OAuth response from backend session
   */
  private async fetchOAuthResponseFromSession(sessionId: string): Promise<void> {
    try {
      const response: any = await this.http.get(`/auth/oauth/get-response?session_id=${sessionId}`).toPromise();
      console.log('OAuth response received from session:', response);
      
      if (response.users && response.chooseLC) {
        // Multiple users - show LC selector
        this.isOAuthMode = true;
        this.oauthData = response;
        this.openLcSelectorModal(response.users);
      } else if (response.token) {
        // Single user - redirect to token login endpoint
        const tokenLoginUrl = `/auth/token-login?token=${encodeURIComponent(response.token)}`;
        console.log('OAuth - Single user, redirecting to token login:', tokenLoginUrl);
        window.location.href = tokenLoginUrl;
      } else if (response.message) {
        this.errorMessage = response.message;
      }
    } catch (error) {
      console.error('Failed to fetch OAuth response from session:', error);
      this.errorMessage = 'Failed to retrieve OAuth response. Please try again.';
    }
  }

  /**
   * Handles the switch LC request by checking for the 'switch' parameter in the URL
   * and opening the LC selector modal if needed
   */
  private handleSwitchLcRequest(): void {
    const urlParams = new URLSearchParams(window.location.search);
    const switchParam = urlParams.get('switch');
    
    if (switchParam !== null) {
      // This is a switch LC request, we need to get the authorized users from the backend
      // and show the LC selector modal
      this.authService.getAuthorizedUsers().subscribe({
        next: (response) => {
          if (response && response.users && response.users.length > 1) {
            // For switch LC, we need to get the stored credentials and use them
            this.authService.getStoredCredentials().subscribe({
              next: (credentials) => {
                if (credentials && credentials.email) {
                  // Store the credentials for use when LC is selected
                  this.email = credentials.email;
                  this.password = credentials.password || ''; // OAuth users may have empty password
                  
                  // Check if this is an OAuth user (empty password)
                  if (!credentials.password) {
                    this.isOAuthMode = true; // Set OAuth mode for switch LC
                    // For OAuth switch LC, we need to get the OAuth data from session
                    this.handleOAuthSwitchLc(response.users);
                  } else {
                    this.isSwitchLcMode = true; // Set flag for regular switch LC mode
                    this.openLcSelectorModal(response.users);
                  }
                } else {
                  console.error('No stored credentials found for switch LC');
                  window.location.href = '/home/index';
                }
              },
              error: (error) => {
                console.error('Error getting stored credentials:', error);
                window.location.href = '/home/index';
              }
            });
          } else {
            // No multiple LCs available, redirect to home
            window.location.href = '/home/index';
          }
        },
        error: (error) => {
          console.error('Error getting authorized users for switch:', error);
          // On error, redirect to home
          window.location.href = '/home/index';
        }
      });
    }
  }

  /**
   * Handles OAuth switch LC by getting OAuth data from session
   */
  private async handleOAuthSwitchLc(users: { id: number; name: string }[]): Promise<void> {
    try {
      // Get OAuth provider from stored credentials
      const credentials = await this.authService.getStoredCredentials().toPromise();
      const oauthProvider = credentials?.oauth_provider || 'microsoft'; // Default to microsoft
      
      // Get OAuth data from session (we need to reconstruct it from the available users)
      // Since we're switching LCs, we know the user is already authenticated via OAuth
      const oauthData = {
        email: this.email,
        provider: oauthProvider,
        users: users,
        chooseLC: true
      };
      
      this.oauthData = oauthData;
      this.openLcSelectorModal(users);
    } catch (error) {
      console.error('Error handling OAuth switch LC:', error);
      this.errorMessage = 'Error switching Learning Center. Please try again.';
    }
  }

  /**
   * Performs OAuth login with selected network ID
   * This method calls the backend to complete OAuth login with the selected LC
   */
  private async performOAuthLoginWithSelectedNetwork(selectedNetworkId: number): Promise<void> {
    this.errorMessage = '';
    this.isLoading = true;
    
    try {
      // Call backend to complete OAuth login with selected network
      const response: any = await this.http.post('/auth/oauth/complete-login', {
        email: this.oauthData.email,
        provider: this.oauthData.provider,
        network_id: selectedNetworkId
      }).toPromise();

      if (response && response.token) {
        // Login successful - redirect to token login endpoint
        this.isLoggedIn = true;
        const tokenLoginUrl = `/auth/token-login?token=${encodeURIComponent(response.token)}`;
        console.log('OAuth - Redirecting to token login:', tokenLoginUrl);
        window.location.href = tokenLoginUrl;
      } else if (response && response.message) {
        this.errorMessage = response.message;
      } else {
        this.errorMessage = 'OAuth login failed. Please try again.';
      }
    } catch (error) {
      console.error('OAuth login error:', error);
      this.errorMessage = 'An error occurred during OAuth login. Please try again.';
    } finally {
      this.isLoading = false;
      this.isOAuthMode = false;
      this.oauthData = null;
    }
  }

  /**
   * Performs login with stored credentials (used for switch LC functionality)
   * This method bypasses the form validation since we already have valid credentials
   */
  private async performLoginWithStoredCredentials(): Promise<void> {
    this.errorMessage = '';
    this.isLoading = true;
    
    try {
      const response = await this.authService.login({
        email: this.email,
        password: this.password,
        network_id: this.selectedNetworkId || undefined
      });

      // Handle API login response (same logic as regular login)
      await this.handleApiLoginResponse(response);
      
      // Reset switch LC mode flag if login was successful
      if (this.isLoggedIn) {
        this.isSwitchLcMode = false;
      }
    } catch (error) {
      console.error('Login error:', error);
      this.errorMessage = 'An error occurred during login. Please try again.';
      this.isSwitchLcMode = false;
    } finally {
      this.isLoading = false;
    }
  }
}
